import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Card,
  Container,
  Typography,
  Box,
  TablePagination,
  TextField,
  CircularProgress,
  Tooltip, IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';


import { styled } from '@mui/material/styles';
import { Buffer } from 'buffer';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';
import { UserListHead, UserListToolbar } from '../user';
import Scrollbar from '../../../components/scrollbar';

const StyledTableCell = styled(TableCell)`
  padding: 16px;
  text-align: center;
`;

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? theme.palette.grey[100]
        : theme.palette.action.hover,
  },
}));


const TABLE_HEAD = [
  // { id: 'userId.userName', label: <b>User Name</b>, alignRight: false },
  { id: 'subscriptionPlanId.subscriptionTitle', label: <b>Subscription Plan</b>, alignRight: false },
  { id: 'typeOfOS', label: <b>OS Type</b>, alignRight: false },
  { id: 'typeOfIp', label: <b>IP Type</b>, alignRight: false },
  { id: 'dashboardName', label: <b>Dashboard Name</b>, alignRight: false },
  { id: 'ipAddress', label: <b>IP:Port Address</b>, alignRight: false },
  // { id: 'portAddress', label: <b>PORT Number</b>, alignRight: false },
  { id: 'isActive', label: <b>Status</b>, alignRight: false },
  { id: 'action', label: <b>Action</b>, alignRight: false },
];


function AddressDataTable() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('userId.userName');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const token = localStorage.getItem('token');

  const [editRowId, setEditRowId] = useState(null);
  const [editedDashboardName, setEditedDashboardName] = useState('');

  const handleEditClick = (row) => {
    setEditRowId(row._id);
    setEditedDashboardName(row.dashboardName);
  };

  const handleSaveClick = async (row) => {
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };

      await axios.post(`${API_BASE_URL}/updateDashboardName`, {
        _id: row._id,
        dashboardName: editedDashboardName,
      }, config);

      fetchData(); // Refresh the data
      setEditRowId(null); // Exit edit mode
    } catch (error) {
      console.error('Error updating dashboard name:', error);
    }
  };



  const navigate = useNavigate();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const token = localStorage.getItem('token');
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.get(`${API_BASE_URL}/getAddressByUserId?userId=${userId}`, config);
      if (response.data.success && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = (_id, e) => {
    e.preventDefault();
    setItemIdToDelete(_id);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      await axios.post(`${API_BASE_URL}/deleteAddress`, { _id: itemIdToDelete }, config);
      fetchData();
      toast.success('Address deleted successfully');
    } catch (error) {
      toast.error('Unable to delete address');
      console.error('Error deleting address:', error);
    } finally {
      setIsLoading(false);
      setItemIdToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setItemIdToDelete(null);
  };

  const handleView = (item) => {
    const userName = item?.typeOfIp === 'public' ? `${item?.userId?.userName}_public` : `${item?.userId?.userName}_proxy`;
    const isEnterprisePlan = item?.subscriptionPlanId?.subscriptionTitle === "Enterprise";

    const data = JSON.stringify({
      dashboardName: item.dashboardName,
      userName,
      ipAddress: item.ipAddress,
      typeOfOS: item?.typeOfOS,
      portAddress: item.portAddress,
      dataRetentionDays: isEnterprisePlan
        ? 45
        : item?.subscriptionPlanId?.dataRetentionDays ?? 0
    });
    const encoded = Buffer.from(data).toString('base64');

    if (item?.typeOfOS === 'linuxOrMac') {
      navigate(`/dashboard/monitorGraph/linuxOrMac/${encoded}`);
    } else {
      navigate(`/dashboard/monitorGraph/windows/${encoded}`);
    }


  };

  const handleRequestSort = (event, property) => {
    if (property === 'actions' || property === 'serial') return;
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };
  const getComparator = (order, orderBy) => (a, b) => {
    const valA = getNestedValue(a, orderBy);
    const valB = getNestedValue(b, orderBy);

    if (valA === undefined || valB === undefined) {
      return 0;
    }

    const aVal = typeof valA === 'string' ? valA.toLowerCase() : valA;
    const bVal = typeof valB === 'string' ? valB.toLowerCase() : valB;

    if (order === 'asc') {
      return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
    }

    return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const filteredData = stableSort(data, getComparator(order, orderBy)).filter((item) =>
    Object.values(item).join(' ').toLowerCase().includes(filterName.toLowerCase())
  );

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== 'string') return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleCancelEdit = () => {
    setEditRowId(null);
    setEditedDashboardName('');
  };

  const getTextColorClass = (transactionType) => {
    switch (transactionType) {
      case 'up':
        return 'green-text';
      case 'down':
        return 'red-text';
      default:
        return '';
    }
  };

  return (
    <Card>
      <Container maxWidth="xl" >
        <Typography
          variant="h6"
          sx={{ marginTop: '15px', fontSize: { xs: '1.5rem', md: '2rem' } }}
        >
          Watch List
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <UserListToolbar
            filterName={filterName}
            onFilterName={handleFilterByName}
            searchName="Search Dashboard Name"
          />
          <Button
            variant="outlined"
            color="primary"
            onClick={fetchData}
            sx={{ mr: 2, mt: 3, textTransform: 'none' }}
          >
            <RefreshIcon sx={{ mr: 1 }} />
            Reload Status
          </Button>

        </Box>

        <Scrollbar>
          <TableContainer sx={{ minWidth: 1000, overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '1rem' }}>
              {isLoading ? (
                <CircularProgress />
              ) : (
                <>
                  <Table>
                    <UserListHead
                      order={order}
                      orderBy={orderBy}
                      headLabel={TABLE_HEAD}
                      rowCount={data.length}
                      onRequestSort={handleRequestSort}
                    />
                    {data.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          <TableCell align="center" colSpan={10}>
                            <Typography variant="body2" color="text.secondary" sx={{ justifyContent: 'center', display: 'flex' }}>
                              No Data Found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    ) : (
                      <TableBody>
                        {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
                          const { _id, userId, subscriptionPlanId, typeOfIp, typeOfOS, dashboardName, ipAddress, portAddress, isActive, instanceStatus } =
                            row;

                          return (
                            <StyledTableRow hover key={_id} tabIndex={-1}>
                              <TableCell padding="normal">{page * rowsPerPage + index + 1}</TableCell>
                              {/* <StyledTableCell>{capitalizeFirst(userId.userName)}</StyledTableCell> */}
                              <StyledTableCell>{capitalizeFirst(subscriptionPlanId.subscriptionTitle)}</StyledTableCell>
                              <StyledTableCell>{capitalizeFirst(typeOfOS)}</StyledTableCell>
                              <StyledTableCell>{capitalizeFirst(typeOfIp)}</StyledTableCell>
                              <StyledTableCell>
                                {editRowId === row._id ? (
                                  <TextField
                                    fullWidth
                                    focused
                                    name="newDashboardName"
                                    value={editedDashboardName}
                                    onChange={(e) => setEditedDashboardName(e.target.value)}
                                  />


                                ) : (
                                  capitalizeFirst(dashboardName)
                                )}
                              </StyledTableCell>

                              <StyledTableCell>{ipAddress} : {portAddress}</StyledTableCell>
                              {/* <StyledTableCell></StyledTableCell> */}
                              <StyledTableCell>
                                <span
                                  className={`balance-cell ${getTextColorClass(instanceStatus)}`}
                                >
                                  {capitalizeFirst(instanceStatus)}
                                </span>

                                <style>
                                  {`
                                    .balance-cell {
                                      display: inline-block;
                                      padding: 4px 10px;
                                      font-weight: bold;
                                      border-radius: 15%;
                                    }

                                    .green-text {
                                      background-color: #bcf3beff;
                                      color: #1b5e20;
                                    }

                                    .red-text {
                                      background-color: #ecc9c9ff;
                                      color: #b71c1c;
                                    }
                                  `}
                                </style>
                              </StyledTableCell>

                              <TableCell align="center">
                                {editRowId === row._id ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'nowrap',
                                      gap: 1,
                                      justifyContent: 'center',
                                      overflowX: 'auto',
                                      maxWidth: '100%',
                                      scrollbarWidth: 'none',
                                      '&::-webkit-scrollbar': { display: 'none' },
                                    }}
                                  >
                                    <Tooltip title="Save" arrow>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleSaveClick(row)}
                                      >
                                        <SaveIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Cancel" arrow>
                                      <IconButton
                                        color="error"
                                        onClick={handleCancelEdit}
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexWrap: 'nowrap',
                                      gap: 1,
                                      justifyContent: 'center',
                                      overflowX: 'auto',
                                      maxWidth: '100%',
                                      scrollbarWidth: 'none',
                                      '&::-webkit-scrollbar': { display: 'none' },
                                    }}
                                  >
                                    <Tooltip title="View Metrics" arrow>
                                      <IconButton
                                        color="info"
                                        onClick={() => handleView(row)}
                                        disabled={!isActive}
                                      >
                                        <AnalyticsIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Edit" arrow>
                                      <IconButton
                                        color="primary"
                                        onClick={() => handleEditClick(row)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title="Delete" arrow>
                                      <IconButton
                                        color="error"
                                        onClick={(e) => handleDelete(_id, e)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                )}
                              </TableCell>

                            </StyledTableRow>
                          );
                        })}

                        {filteredData.length === 0 && (
                          <StyledTableRow>
                            <StyledTableCell colSpan={9} align="center">
                              <Typography variant="subtitle1" textAlign='center' sx={{ py: 3 }}>
                                {filterName
                                  ? `No results found for "${filterName}"`
                                  : 'No data available.'}
                              </Typography>
                            </StyledTableCell>
                          </StyledTableRow>
                        )}
                      </TableBody>
                    )}

                  </Table>
                </>
              )}
            </div>
          </TableContainer>
        </Scrollbar>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Container>
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this?"
      />
    </Card>
  );
}

export default AddressDataTable;
